import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import { CONTRATS, CONVENTIONS, PERIODICITES, uid } from '../utils/helpers';

const normalize = s => (s || '').trim().toUpperCase().replace(/\s+/g, ' ');

const EJS_SERVICE  = process.env.REACT_APP_EMAILJS_SERVICE_ID        || '';
const EJS_ADHESION = process.env.REACT_APP_EMAILJS_TEMPLATE_ADHESION  || '';
const EJS_NOTIF    = process.env.REACT_APP_EMAILJS_NOTIF_TEMPLATE_ID  || '';
const EJS_KEY      = process.env.REACT_APP_EMAILJS_PUBLIC_KEY          || '';

const METIERS = ['Aide-soignant(e)','Infirmier(ère)','Éducateur(rice) spécialisé(e)','Aide médico-psychologique','Accompagnant éducatif et social','Psychologue','Assistant(e) de service social','Chef de service','Animateur(rice)','Agent de service hospitalier','Moniteur(rice) éducateur(rice)','Médecin','Kinésithérapeute','Ergothérapeute','Orthophoniste','Secrétaire médicale','Cuisinier(ère)','Agent de maintenance'];

const STEPS = ['Identité','Situation pro','Paiement','Document signé','Récapitulatif'];

function generateRUM(nom, year, seq) {
  const letters = (nom || 'XXX').replace(/[^A-Z]/gi,'').toUpperCase().slice(0,3).padEnd(3,'X');
  return `SDASSP-${year}-${letters}-${String(seq).padStart(3,'0')}`;
}

function Stepper({ current }) {
  return (
    <div className="stepper">
      {STEPS.map((label, i) => (
        <React.Fragment key={i}>
          {i > 0 && <div className={`stepper-line${i <= current ? ' done' : ''}`} />}
          <div className="stepper-step">
            <div className={`stepper-dot ${i < current ? 'done' : i === current ? 'current' : 'pending'}`}>
              {i < current ? '✓' : i + 1}
            </div>
            <div className={`stepper-label${i === current ? ' current' : ''}`}>{label}</div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

const MOIS_NOMS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

export default function NouvelleAdhesion({ etablissements, demandes, saveDemande, saveAdherent, saveEtablissement, onSubmitted, currentRole, currentUsername, currentUserEmail, addJournal, users, submitComplementResponse }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    id: uid(),
    statut: 'brouillon',
    civilite: 'M.',
    nom: '', prenom: '', dateNaissance: '',
    adresse: '', codePostal: '', ville: '',
    telephone: '', email: '',
    etablissementId: '', etablissementLibre: '', metier: '',
    contrat: 'CDI', convention: 'CCN 51', coefficient: '', dateEntree: '',
    modePaiement: 'Prélèvement auto',
    rum: '', titulaireCompte: '', banque: '', iban: '', bic: '',
    periodicite: 'Mensuelle', jourPrelevement: '5', montant: '', moisEcheance: '',
    dateSignature: new Date().toISOString().slice(0,10), consentement: false,
    document: null, documentStatut: 'en_attente', notesDelegue: '',
    delegueNom: '', delegueEmail: '', createdBy: '',
    dateCreation: new Date().toISOString(),
  });
  const [metierOpen, setMetierOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [complementModal, setComplementModal] = useState(null);
  const [complementText, setComplementText] = useState('');
  const fileRef = useRef();
  const fileRef2 = useRef();

  const f = k => v => setForm(p => ({ ...p, [k]: v }));

  const etabName = id => etablissements.find(e => e.id === id)?.nom || '';

  // Auto-fill RUM when name changes — uniquement pour prélèvement
  const ensureRUM = (nom) => {
    if (form.modePaiement !== 'Prélèvement auto') return;
    if (!form.rum) {
      const year = new Date().getFullYear();
      const seq = (demandes?.length || 0) + 1;
      const rum = generateRUM(nom, year, seq);
      setForm(p => ({ ...p, rum }));
    }
  };

  const handleNomBlur = () => ensureRUM(form.nom);

  // IBAN guided input — format by blocks of 4
  const handleIBAN = (v) => {
    const raw = v.replace(/\s/g,'').toUpperCase();
    const formatted = raw.match(/.{1,4}/g)?.join(' ') || raw;
    f('iban')(formatted);
  };

  // Document upload
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      setForm(p => ({ ...p, document: { name: file.name, type: file.type, data: ev.target.result } }));
    };
    reader.readAsDataURL(file);
  };

  const handleFile2 = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      setForm(p => ({ ...p, documentRib: { name: file.name, type: file.type, data: ev.target.result } }));
    };
    reader.readAsDataURL(file);
  };

  const canNext = () => {
    if (step === 0) return form.nom.trim() && form.prenom.trim() && form.telephone.trim();
    if (step === 1) return ((form.etablissementId && form.etablissementId !== '__libre__') || form.etablissementLibre.trim()) && form.metier.trim();
    if (step === 2) {
      if (form.modePaiement === 'Chèque') return !!form.montant;
      if (form.modePaiement === 'Virement') return !!form.montant;
      return form.iban.trim().length >= 14 && !!form.montant && form.consentement;
    }
    if (step === 3) return true;
    return true;
  };

  const handleSubmit = () => {
    let resolvedEtabId = form.etablissementId === '__libre__' ? '' : form.etablissementId;
    let resolvedEtabNom = '';

    if (form.etablissementId === '__libre__' && form.etablissementLibre.trim()) {
      const nom = form.etablissementLibre.trim();
      const existing = etablissements.find(e => normalize(e.nom) === normalize(nom));
      if (existing) {
        resolvedEtabId = existing.id;
      } else if (saveEtablissement) {
        const newId = uid();
        saveEtablissement({ id: newId, nom, delegue: currentUsername || '', createdAt: new Date().toISOString() });
        resolvedEtabId = newId;
        addJournal('adhesion', `Nouvel établissement créé : ${nom}`);
      }
      resolvedEtabNom = nom;
    } else {
      resolvedEtabNom = etablissements.find(e => e.id === resolvedEtabId)?.nom || '';
    }

    const finalDemande = {
      ...form,
      etablissementId: resolvedEtabId,
      etablissementLibre: resolvedEtabNom,
      statut: 'en_attente',
      createdBy: currentUsername || '',
      dateCreation: new Date().toISOString(),
    };
    console.log('Nouvelle demande créée :', finalDemande);
    saveDemande(finalDemande);
    addJournal('adhesion', `Nouvelle demande : ${form.prenom} ${form.nom}`);
    const etab = resolvedEtabNom || resolvedEtabId;
    if (currentUserEmail && EJS_SERVICE && EJS_ADHESION && EJS_KEY) {
      emailjs.send(EJS_SERVICE, EJS_ADHESION, {
        to_email: currentUserEmail,
        delegue_name: currentUsername || 'Délégué',
        adherent_prenom: form.prenom,
        adherent_nom: form.nom,
        etablissement: etab || '—',
        date_demande: new Date().toLocaleDateString('fr-FR'),
      }, EJS_KEY).catch(() => {});
    }
    if (EJS_SERVICE && EJS_NOTIF && EJS_KEY) {
      (users || []).filter(u => u.isAdmin && u.email && u.username !== currentUsername).forEach(admin => {
        emailjs.send(EJS_SERVICE, EJS_NOTIF, {
          to_email: admin.email,
          destinataire_name: admin.username,
          sujet: `📝 Nouvelle adhésion à traiter — ${form.prenom} ${form.nom}`,
          message_body: `Une nouvelle demande d'adhésion a été soumise par ${currentUsername || 'un délégué'}.

Adhérent : ${form.prenom} ${form.nom}
Établissement : ${etab || '—'}
Date : ${new Date().toLocaleDateString('fr-FR')}

Connectez-vous pour traiter cette demande.`,
        }, EJS_KEY).catch(() => {});
      });
    }
    setSubmitted(true);
    onSubmitted && onSubmitted();
  };

  if (submitted) {
    return (
      <div style={{ maxWidth: 520, margin: '60px auto', textAlign: 'center' }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
        <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Demande soumise !</div>
        <div style={{ color: '#666', marginBottom: 24 }}>
          <>La demande d'adhésion de <strong>{form.prenom} {form.nom}</strong> est en attente de validation.</>
        </div>
        <button className="btn btn-primary" onClick={() => { setSubmitted(false); setStep(0); setForm({ id: uid(), statut:'brouillon', civilite:'M.', nom:'',prenom:'',dateNaissance:'',adresse:'',codePostal:'',ville:'',telephone:'',email:'',etablissementId:'',etablissementLibre:'',metier:'',contrat:'CDI',convention:'CCN 51',coefficient:'',dateEntree:'',rum:'',titulaireCompte:'',banque:'',iban:'',bic:'',periodicite:'Mensuelle',jourPrelevement:'5',montant:'',moisEcheance:'',modePaiement:'Prélèvement auto',dateSignature:new Date().toISOString().slice(0,10),consentement:false,document:null,documentRib:null,documentStatut:'en_attente',notesDelegue:'',dateCreation:new Date().toISOString() }); }}>
          + Nouvelle demande
        </button>
      </div>
    );
  }

  const delegueInfo = form.etablissementId ? etablissements.find(e => e.id === form.etablissementId)?.delegue : '';

  const mesDemandes = (demandes || []).filter(d => d.createdBy === currentUsername && d.statut !== 'refuse');
  const aComplementer = mesDemandes.filter(d => d.statut === 'complement');

  const handleSubmitComplement = () => {
    if (!complementModal || !complementText.trim()) return;
    submitComplementResponse(complementModal.id, complementText.trim());
    if (EJS_SERVICE && EJS_NOTIF && EJS_KEY) {
      (users || []).filter(u => u.isAdmin && u.email).forEach(admin => {
        emailjs.send(EJS_SERVICE, EJS_NOTIF, {
          to_email: admin.email,
          destinataire_name: admin.username,
          sujet: `💬 Complément reçu — ${complementModal.prenom} ${complementModal.nom}`,
          message_body: `Le délégué ${currentUsername} a soumis le complément demandé pour ${complementModal.prenom} ${complementModal.nom}.

Réponse : ${complementText.trim()}

Connectez-vous pour traiter cette demande.`,
        }, EJS_KEY).catch(() => {});
      });
    }
    setComplementModal(null);
    setComplementText('');
  };

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>

      {/* ── Demandes en attente de complément ── */}
      {aComplementer.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color:'#CC0000', marginBottom: 10 }}>💬 Complément demandé</div>
          {aComplementer.map(d => (
            <div key={d.id} style={{ background:'#fff8e1', border:'1.5px solid #ffe082', borderRadius:10, padding:'12px 16px', marginBottom:10 }}>
              <div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>{d.prenom} {d.nom}</div>
              <div style={{ fontSize:12, color:'#888', marginBottom:8 }}>Soumis le {new Date(d.dateCreation).toLocaleDateString('fr-FR')}</div>
              {d.messageComplement && (
                <div style={{ background:'#fff3cd', borderRadius:6, padding:'8px 10px', fontSize:13, color:'#555', marginBottom:10 }}>
                  <strong>Message :</strong> {d.messageComplement}
                </div>
              )}
              <button className="btn btn-primary btn-sm" onClick={() => { setComplementModal(d); setComplementText(''); }}>
                💬 Répondre au complément
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Modal réponse complément */}
      {complementModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:24, maxWidth:480, width:'100%' }}>
            <div style={{ fontWeight:700, fontSize:16, marginBottom:4 }}>💬 Soumettre le complément</div>
            <div style={{ fontSize:13, color:'#888', marginBottom:16 }}>{complementModal.prenom} {complementModal.nom}</div>
            {complementModal.messageComplement && (
              <div style={{ background:'#f9f9f9', borderRadius:6, padding:'8px 10px', fontSize:13, color:'#555', marginBottom:12 }}>
                <strong>Demande :</strong> {complementModal.messageComplement}
              </div>
            )}
            <textarea className="form-control" rows={5} value={complementText} onChange={e => setComplementText(e.target.value)}
              placeholder="Décrivez les informations complémentaires demandées..." style={{ marginBottom:16 }} />
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="btn btn-outline" onClick={() => { setComplementModal(null); setComplementText(''); }}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSubmitComplement} disabled={!complementText.trim()}>Envoyer</button>
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <div><div className="page-title">➕ Nouvelle adhésion</div><div className="page-subtitle">Formulaire de demande d'adhésion SDASSP FO 974</div></div>
        <a
          href="/bordereau-adhesion.pdf"
          download="Bordereau-Adhesion-SDASSP-FO.pdf"
          className="btn btn-outline btn-sm"
          style={{ display:'flex', alignItems:'center', gap:6, textDecoration:'none' }}
        >
          📥 Bordereau d'adhésion
        </a>
      </div>

      <Stepper current={step} />

      <div className="card">
        {/* ── STEP 0 : Identité ── */}
        {step === 0 && (
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>👤 Identité de l'adhérent</div>
            <div className="form-group">
              <label className="form-label">Civilité</label>
              <div style={{ display: 'flex', gap: 10 }}>
                {['M.','Mme'].map(c => (
                  <label key={c} style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', padding:'7px 16px', borderRadius:6, border:`1px solid ${form.civilite===c?'#CC0000':'#ddd'}`, background:form.civilite===c?'#fff5f5':'#fff', fontWeight:form.civilite===c?700:400 }}>
                    <input type="radio" name="civilite" checked={form.civilite===c} onChange={() => f('civilite')(c)} style={{ accentColor:'#CC0000' }} /> {c}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Nom *</label>
                <input className="form-control" value={form.nom} onChange={e => f('nom')(e.target.value.toUpperCase())} onBlur={handleNomBlur} placeholder="NOM" />
              </div>
              <div className="form-group">
                <label className="form-label">Prénom *</label>
                <input className="form-control" value={form.prenom} onChange={e => f('prenom')(e.target.value)} placeholder="Prénom" />
              </div>
              <div className="form-group">
                <label className="form-label">Date de naissance</label>
                <input className="form-control" type="date" value={form.dateNaissance} onChange={e => f('dateNaissance')(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Téléphone mobile *</label>
                <input className="form-control" type="tel" value={form.telephone} onChange={e => f('telephone')(e.target.value)} placeholder="0692 XX XX XX" />
              </div>
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Adresse (n° et rue)</label>
                <input className="form-control" value={form.adresse} onChange={e => f('adresse')(e.target.value)} placeholder="12 Rue des Flamboyants" />
              </div>
              <div className="form-group">
                <label className="form-label">Code postal</label>
                <input className="form-control" value={form.codePostal} onChange={e => f('codePostal')(e.target.value)} placeholder="97400" maxLength={5} />
              </div>
              <div className="form-group">
                <label className="form-label">Ville</label>
                <input className="form-control" value={form.ville} onChange={e => f('ville')(e.target.value)} placeholder="Saint-Denis" />
              </div>
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Email</label>
                <input className="form-control" type="email" value={form.email} onChange={e => f('email')(e.target.value)} placeholder="prenom.nom@email.re" />
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 1 : Situation pro ── */}
        {step === 1 && (
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>💼 Situation professionnelle</div>
            <div className="grid-2">
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Établissement / Employeur *</label>
                <select className="form-control" value={form.etablissementId} onChange={e => { f('etablissementId')(e.target.value); f('etablissementLibre')(''); }}>
                  <option value="">— Sélectionner un établissement —</option>
                  {etablissements.map(e => <option key={e.id} value={e.id}>{e.nom}</option>)}
                  <option value="__libre__">Autre (saisie libre)</option>
                </select>
                {form.etablissementId === '__libre__' && (
                  <input className="form-control" style={{ marginTop: 6 }} value={form.etablissementLibre} onChange={e => f('etablissementLibre')(e.target.value)} placeholder="Nom de l'établissement" />
                )}
              </div>
              {delegueInfo && (
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Délégué syndical du site (auto-rempli)</label>
                  <input className="form-control" value={delegueInfo} readOnly style={{ background:'#f8f8f8', color:'#666' }} />
                </div>
              )}
              <div className="form-group autocomplete-wrap" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Métier / Poste *</label>
                <input className="form-control" value={form.metier}
                  onChange={e => { f('metier')(e.target.value); setMetierOpen(true); }}
                  onFocus={() => setMetierOpen(true)}
                  onBlur={() => setTimeout(() => setMetierOpen(false), 150)}
                  placeholder="Ex : Aide-soignant(e), Infirmier(ère)..." />
                {metierOpen && form.metier.length > 0 && (
                  <div className="autocomplete-list">
                    {METIERS.filter(m => m.toLowerCase().includes(form.metier.toLowerCase())).map(m => (
                      <div key={m} className="autocomplete-item" onMouseDown={() => { f('metier')(m); setMetierOpen(false); }}>{m}</div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Type de contrat</label>
                <select className="form-control" value={form.contrat} onChange={e => f('contrat')(e.target.value)}>
                  {CONTRATS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Convention collective</label>
                <select className="form-control"
                  value={CONVENTIONS.slice(0,-1).includes(form.convention) ? form.convention : 'Autre'}
                  onChange={e => f('convention')(e.target.value === 'Autre' ? '' : e.target.value)}>
                  {CONVENTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {!CONVENTIONS.slice(0,-1).includes(form.convention) && (
                  <input className="form-control" style={{ marginTop: 6 }}
                    value={form.convention}
                    onChange={e => f('convention')(e.target.value)}
                    placeholder="Saisir la convention collective..." />
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Coefficient / Échelon</label>
                <input className="form-control" value={form.coefficient} onChange={e => f('coefficient')(e.target.value)} placeholder="Ex : 376" />
              </div>
              <div className="form-group">
                <label className="form-label">Date d'entrée dans l'emploi</label>
                <input className="form-control" type="date" value={form.dateEntree} onChange={e => f('dateEntree')(e.target.value)} />
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 2 : Paiement ── */}
        {step === 2 && (
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>💳 Mode de paiement</div>

            {/* Mode selector */}
            <div className="form-group">
              <label className="form-label">Mode de paiement *</label>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {['Prélèvement auto','Virement','Chèque'].map(m => (
                  <label key={m} style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', padding:'8px 16px', borderRadius:6, border:`1.5px solid ${form.modePaiement===m?'#CC0000':'#ddd'}`, background:form.modePaiement===m?'#fff5f5':'#fff', fontWeight:form.modePaiement===m?700:400, fontSize:13 }}>
                    <input type="radio" name="modePaiement" checked={form.modePaiement===m} onChange={() => f('modePaiement')(m)} style={{ accentColor:'#CC0000' }} /> {m}
                  </label>
                ))}
              </div>
            </div>

            <div className="grid-2" style={{ marginTop:16 }}>
              <div className="form-group">
                <label className="form-label">Montant par échéance (€) *</label>
                <input className="form-control" type="number" min="0" step="0.50" value={form.montant} onChange={e => f('montant')(e.target.value)} placeholder="Ex : 18.50" />
              </div>
              <div className="form-group">
                <label className="form-label">Périodicité</label>
                <select className="form-control" value={form.periodicite} onChange={e => f('periodicite')(e.target.value)}>
                  {PERIODICITES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              {form.modePaiement === 'Prélèvement auto' && (
                <div className="form-group">
                  <label className="form-label">Jour de prélèvement</label>
                  <select className="form-control" value={form.jourPrelevement} onChange={e => f('jourPrelevement')(e.target.value)}>
                    {[['1','1er du mois'],['3','3 du mois'],['5','5 du mois'],['10','10 du mois'],['30','30 du mois']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              )}
              {form.periodicite === 'Annuelle' && (
                <div className="form-group">
                  <label className="form-label">Mois d'échéance annuelle</label>
                  <select className="form-control" value={form.moisEcheance} onChange={e => f('moisEcheance')(e.target.value)}>
                    <option value="">-- Sélectionner --</option>
                    {MOIS_NOMS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              )}
            </div>

            {/* IBAN/BIC — prélèvement uniquement */}
            {form.modePaiement === 'Prélèvement auto' && (
              <div className="grid-2">
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                    <label className="form-label">Référence Unique de Mandat (RUM)</label>
                    <div style={{ display:'flex', gap:6 }}>
                      <input className="form-control" value={form.rum} onChange={e => f('rum')(e.target.value)} style={{ flex:1, fontFamily:'monospace', fontSize:13 }} />
                      <button className="btn btn-outline btn-sm" onClick={() => f('rum')(generateRUM(form.nom, new Date().getFullYear(), (demandes?.length||0)+1))}>↺ Générer</button>
                    </div>
                  </div>
                <div className="form-group">
                  <label className="form-label">Titulaire du compte bancaire</label>
                  <input className="form-control" value={form.titulaireCompte} onChange={e => f('titulaireCompte')(e.target.value)} placeholder={`${form.civilite} ${form.prenom} ${form.nom}`.trim()} />
                </div>
                <div className="form-group">
                  <label className="form-label">Nom de la banque</label>
                  <input className="form-control" value={form.banque} onChange={e => f('banque')(e.target.value)} placeholder="Ex : Crédit Agricole, BNP..." />
                </div>
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">IBAN *</label>
                  <input className="form-control" value={form.iban} onChange={e => handleIBAN(e.target.value)} placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX" style={{ fontFamily:'monospace', letterSpacing:1 }} maxLength={34} />
                </div>
                <div className="form-group">
                  <label className="form-label">BIC / SWIFT</label>
                  <input className="form-control" value={form.bic} onChange={e => f('bic')(e.target.value.toUpperCase())} placeholder="Ex : AGRIFRPP" style={{ fontFamily:'monospace' }} />
                </div>
                <div className="form-group">
                  <label className="form-label">Date de signature</label>
                  <input className="form-control" type="date" value={form.dateSignature} onChange={e => f('dateSignature')(e.target.value)} />
                </div>
              </div>
            )}

            {form.modePaiement === 'Virement' && (
              <div style={{ marginTop: 12, padding:'10px 14px', background:'#eff6ff', borderRadius:8, border:'1px solid #bae6fd', fontSize:13, color:'#1d4ed8' }}>
                ℹ️ Pour un virement, l'adhérent effectuera lui-même le virement sur le compte du syndicat. Aucun RIB n'est nécessaire.
              </div>
            )}
            {form.modePaiement === 'Prélèvement auto' && (
              <div style={{ marginTop: 8, padding:'12px 14px', background:'#fff8e1', borderRadius:8, border:'1px solid #ffe082', display:'flex', gap:10, alignItems:'flex-start' }}>
                <input type="checkbox" id="consent" checked={form.consentement} onChange={e => f('consentement')(e.target.checked)} style={{ marginTop:2, accentColor:'#CC0000', width:16, height:16, flexShrink:0 }} />
                <label htmlFor="consent" style={{ fontSize:13, color:'#555', cursor:'pointer', lineHeight:1.5 }}>
                  <strong>J'autorise le SDASSP FO Réunion</strong> à initier des prélèvements SEPA sur mon compte bancaire selon la périodicité et le montant indiqués ci-dessus.
                </label>
              </div>
            )}
          </div>
        )}

        {/* ── STEP 3 : Document ── */}
        {step === 3 && (
          <div>
            {/* Prélèvement : mandat SEPA obligatoire + RIB optionnel */}
            {form.modePaiement === 'Prélèvement auto' && (
              <>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>📎 Mandat SEPA signé</div>
                <div className="upload-zone" onClick={() => fileRef.current?.click()}>
                  {form.document ? (
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>✅ {form.document.name}</div>
                      {form.document.type.startsWith('image/') ? (
                        <img src={form.document.data} alt="Aperçu" className="upload-preview" />
                      ) : (
                        <div style={{ padding:'20px', background:'#f5f5f5', borderRadius:6, color:'#666', fontSize:13 }}>📄 {form.document.name}</div>
                      )}
                      <button className="btn btn-outline btn-sm" style={{ marginTop:10 }} onClick={e => { e.stopPropagation(); f('document')(null); }}>🗑️ Supprimer</button>
                    </div>
                  ) : (
                    <div><div style={{ fontSize: 32, marginBottom: 8 }}>📤</div><div style={{ fontSize: 14, fontWeight: 600 }}>Mandat SEPA signé</div><div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>PDF, JPG ou PNG</div></div>
                  )}
                </div>
                <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display:'none' }} onChange={handleFile} />

                <div style={{ marginTop: 20, fontSize: 15, fontWeight: 700, marginBottom: 12 }}>� RIB (optionnel)</div>
                <div className="upload-zone" onClick={() => fileRef2.current?.click()} style={{ borderStyle: 'dashed', opacity: 0.8 }}>
                  {form.documentRib ? (
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>✅ {form.documentRib.name}</div>
                      {form.documentRib.type.startsWith('image/') ? (
                        <img src={form.documentRib.data} alt="RIB" className="upload-preview" />
                      ) : (
                        <div style={{ padding:'20px', background:'#f5f5f5', borderRadius:6, color:'#666', fontSize:13 }}>📄 {form.documentRib.name}</div>
                      )}
                      <button className="btn btn-outline btn-sm" style={{ marginTop:10 }} onClick={e => { e.stopPropagation(); f('documentRib')(null); }}>🗑️ Supprimer</button>
                    </div>
                  ) : (
                    <div><div style={{ fontSize: 28, marginBottom: 8 }}>🏦</div><div style={{ fontSize: 13, color:'#888' }}>Joindre un RIB (optionnel)</div></div>
                  )}
                </div>
                <input ref={fileRef2} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display:'none' }} onChange={handleFile2} />
              </>
            )}

            {/* Virement : document optionnel */}
            {form.modePaiement === 'Virement' && (
              <>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>📎 Document (optionnel)</div>
                <div style={{ fontSize: 13, color:'#666', marginBottom: 12 }}>Aucun document formel requis pour un virement. Vous pouvez joindre un document si nécessaire.</div>
                <div className="upload-zone" onClick={() => fileRef.current?.click()} style={{ borderStyle:'dashed', opacity:0.8 }}>
                  {form.document ? (
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>✅ {form.document.name}</div>
                      {form.document.type.startsWith('image/') ? (
                        <img src={form.document.data} alt="Aperçu" className="upload-preview" />
                      ) : (
                        <div style={{ padding:'20px', background:'#f5f5f5', borderRadius:6, color:'#666', fontSize:13 }}>📄 {form.document.name}</div>
                      )}
                      <button className="btn btn-outline btn-sm" style={{ marginTop:10 }} onClick={e => { e.stopPropagation(); f('document')(null); }}>🗑️ Supprimer</button>
                    </div>
                  ) : (
                    <div><div style={{ fontSize: 32, marginBottom: 8 }}>📤</div><div style={{ fontSize: 13, color:'#888' }}>Document optionnel — PDF, JPG ou PNG</div></div>
                  )}
                </div>
                <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display:'none' }} onChange={handleFile} />
              </>
            )}

            {/* Chèque : photo du chèque */}
            {form.modePaiement === 'Chèque' && (
              <>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>📸 Photo du chèque</div>
                <div style={{ fontSize: 13, color:'#666', marginBottom: 12 }}>Déposez une photo ou scan du chèque libellé au nom du SDASSP FO Réunion.</div>
                <div className="upload-zone" onClick={() => fileRef.current?.click()}>
                  {form.document ? (
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>✅ {form.document.name}</div>
                      {form.document.type.startsWith('image/') ? (
                        <img src={form.document.data} alt="Chèque" className="upload-preview" />
                      ) : (
                        <div style={{ padding:'20px', background:'#f5f5f5', borderRadius:6, color:'#666', fontSize:13 }}>📄 {form.document.name}</div>
                      )}
                      <button className="btn btn-outline btn-sm" style={{ marginTop:10 }} onClick={e => { e.stopPropagation(); f('document')(null); }}>🗑️ Supprimer</button>
                    </div>
                  ) : (
                    <div><div style={{ fontSize: 32, marginBottom: 8 }}>📸</div><div style={{ fontSize: 14, fontWeight: 600 }}>Photo ou scan du chèque</div><div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>JPG, PNG ou PDF</div></div>
                  )}
                </div>
                <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display:'none' }} onChange={handleFile} />
              </>
            )}

            <div className="form-group" style={{ marginTop: 20 }}>
              <label className="form-label">Notes du délégué (optionnel)</label>
              <textarea className="form-control" value={form.notesDelegue} onChange={e => f('notesDelegue')(e.target.value)} rows={3} placeholder="Informations complémentaires pour le trésorier..." />
            </div>
          </div>
        )}

        {/* ── STEP 4 : Récapitulatif ── */}
        {step === 4 && (
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>📋 Récapitulatif de la demande</div>

            {[
              { title:'👤 Identité', rows:[['Civilité', form.civilite],['Nom / Prénom', `${form.nom} ${form.prenom}`],['Date de naissance', form.dateNaissance || '—'],['Adresse', [form.adresse, form.codePostal, form.ville].filter(Boolean).join(', ') || '—'],['Téléphone', form.telephone || '—'],['Email', form.email || '—']] },
              { title:'💼 Situation professionnelle', rows:[['Établissement', form.etablissementId ? etabName(form.etablissementId) : form.etablissementLibre || '—'],['Métier', form.metier || '—'],['Contrat', form.contrat],['Convention', form.convention],['Coefficient', form.coefficient || '—'],['Date d\'entrée', form.dateEntree || '—']] },
              { title:'💳 Paiement', rows:[['Mode', form.modePaiement],['Périodicité', form.periodicite],['Montant', form.montant ? `${form.montant} €` : '—'], ...(form.modePaiement === 'Prélèvement auto' ? [['RUM', form.rum || '—'],['Titulaire', form.titulaireCompte || '—'],['Banque', form.banque || '—'],['IBAN', form.iban || '—'],['BIC', form.bic || '—'],['Jour prélèvement', form.jourPrelevement ? `${form.jourPrelevement} du mois` : '—'],['Date signature', form.dateSignature || '—']] : [])] },
            ].map(section => (
              <div key={section.title} style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color:'#CC0000', marginBottom: 8 }}>{section.title}</div>
                <div style={{ background:'#f9f9f9', borderRadius:8, overflow:'hidden', border:'1px solid #eee' }}>
                  {section.rows.map(([label, val], i) => (
                    <div key={i} style={{ display:'flex', padding:'7px 12px', borderBottom: i < section.rows.length-1 ? '1px solid #f0f0f0' : 'none' }}>
                      <div style={{ width:160, flexShrink:0, fontSize:12, color:'#888', fontWeight:600 }}>{label}</div>
                      <div style={{ fontSize:13, color:'#222', fontFamily: label==='IBAN'||label==='BIC'||label==='RUM' ? 'monospace' : 'inherit' }}>{val}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {form.document && (
              <div style={{ background:'#e8f5e9', padding:'10px 14px', borderRadius:8, fontSize:13, color:'#2e7d32', marginBottom:16 }}>
                ✅ Document joint : <strong>{form.document.name}</strong>
              </div>
            )}

            <div className="alert alert-warning">
              <span>⚠️</span>
              <div>En cliquant sur <strong>"Soumettre la demande"</strong>, cette demande sera transmise au trésorier pour validation. Elle apparaîtra en statut <em>"En attente de traitement"</em> jusqu'à décision.</div>
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div style={{ display:'flex', justifyContent:'space-between', marginTop:24, paddingTop:16, borderTop:'1px solid #f0f0f0' }}>
          <button className="btn btn-outline" onClick={() => setStep(s => s - 1)} disabled={step === 0}>← Précédent</button>
          {step < 4 ? (
            <button className="btn btn-primary" onClick={() => setStep(s => s + 1)} disabled={!canNext()}>
              Suivant →
            </button>
          ) : (
            <button className="btn btn-primary" style={{ background:'#2e7d32' }} onClick={handleSubmit}>
              ✅ Soumettre la demande
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
